import { OptionProps } from 'antd/es/mentions';
import {
  PaceTaskScreen as PaceTaskScreenEnum,
  Undefinable,
} from 'providence-types';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import { ApiMethods } from '@models/enums';
import {
  MainBrowseFilter,
  PaginatedResponse,
  ProjectBrowse,
  ProjectCreateRequestData,
  ProjectDetails,
  ProjectPaceTask,
  ProjectPaceTaskData,
  ProjectUpdateRequestData,
  SiteBrowse,
} from '@models/interfaces';
import {
  FileType,
  ProjectDetailsWithoutCategories,
  UpdateProjectRADData,
} from '@models/types';
import {
  BreadcrumbsActions,
  ProjectActions,
  SiteActions,
} from '@store/actions';
import { ProjectCategoriesEffects } from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class ProjectHttpService extends CommonHttpService {
  @StoredRequest(ProjectActions.getProjectBrowseDataAction)
  getProjectBrowse(
    params: MainBrowseFilter,
  ): Promise<PaginatedResponse<ProjectBrowse>> {
    return this.http.callApi.get(`${this.apiUrl}/projects`, null, {
      params,
    });
  }

  @StoredRequest(ProjectActions.getProjectAction, BreadcrumbsActions.project)
  async getProject(id: string): Promise<ProjectDetailsWithoutCategories> {
    const response = await this.http.callApi.get(
      `${this.apiUrl}/projects/${id}`,
    );

    return ProjectCategoriesEffects.setProjectCategories(response);
  }

  @StoredRequest(ProjectActions.getProvidenceAgent)
  getProvidenceAgent(id: string): Promise<Undefinable<string>> {
    return this.http.callApi.get(
      `${this.apiUrl}/projects/${id}/providence-agent`,
    );
  }

  @StoredRequest(ProjectActions.getProvidenceAgents)
  getProvidenceAgents(): Promise<OptionProps[]> {
    return this.http.callApi.get(`${this.apiUrl}/projects/providence-agents`);
  }

  @StoredRequest(SiteActions.getSiteAction)
  getSiteByProjectId(id: string): Promise<SiteBrowse> {
    return this.http.callApi.get(`${this.apiUrl}/projects/${id}/site`, null);
  }

  importProjects(file: FileType): Promise<void> {
    return this.uploadFile(
      ApiMethods.Post,
      `${this.apiUrl}/projects/import`,
      file,
    ) as Promise<void>;
  }

  importUpdateProjects(file: FileType): Promise<void> {
    return this.uploadFile(
      ApiMethods.Patch,
      `${this.apiUrl}/projects/import`,
      file,
    ) as Promise<void>;
  }

  importProjectPACEDates(file: File): Promise<void> {
    return this.uploadFile(
      ApiMethods.Patch,
      `${this.apiUrl}/projects/import/pace-dates`,
      file,
    ) as Promise<void>;
  }

  siteOnValidation(id: string): Promise<boolean> {
    return this.http.callApi.get(
      `${this.apiUrl}/projects/site-on-validation`,
      null,
      {
        params: { id },
      },
    );
  }

  createProject(body: ProjectCreateRequestData): Promise<void> {
    return this.http.callApi.post(`${this.apiUrl}/projects`, body);
  }

  @StoredRequest(ProjectActions.updateProjectAction)
  updateProject(
    id: string,
    body: ProjectUpdateRequestData,
  ): Promise<ProjectDetails> {
    return this.http.callApi.patch(`${this.apiUrl}/projects`, body, {
      params: { id },
    });
  }

  @StoredRequest(ProjectActions.updateProjectPaceTasksAction)
  updateProjectPaceTasks(
    id: string,
    screen: PaceTaskScreenEnum,
    body: ProjectPaceTaskData[],
  ): Promise<ProjectPaceTask[]> {
    return this.http.callApi.patch(
      `${this.apiUrl}/projects/pace-task-data`,
      body,
      {
        params: { id, screen },
      },
    );
  }

  async deleteProject(id: string): Promise<void> {
    await this.http.callApi.delete(`${this.apiUrl}/projects`, null, {
      params: { id },
    });
  }

  getProjectProjectCategoryValidation(
    id: string,
    field: 'oracleProjectNumber' | 'PACEProjectNumber',
    value: string,
  ): Promise<boolean> {
    return this.http.callApi.get(
      `${this.apiUrl}/projects/categories/validation`,
      null,
      {
        params: {
          id,
          field,
          value,
        },
      },
    );
  }

  @StoredRequest(ProjectActions.updateProjectRADAction)
  async updateProjectRAD(
    id: string,
    body: UpdateProjectRADData,
  ): Promise<UpdateProjectRADData> {
    await this.http.callApi.patch(`${this.apiUrl}/projects/rad`, body, {
      params: { id },
    });

    return body;
  }
}
